import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { useSettings } from '../../context/SettingsContext';
import BundleProductImageManage from './BundleProductImageManage';
import BundleProductRelationManage from './BundleProductRelationManage';
import BundleProductAttributeValuePage from '../bundleproductattributevalue/BundleProductAttributeValuePage';
import '../css/Category.css';

const BundleProductEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { currencySymbol } = useSettings();
  const [form, setForm] = useState({
    CategoryId: '',
    BrandId: '',
    ProductName: '',
    SKU: '',
    Price: '0.00',
    ShortDescription: '',
    FullDescription: '',
    Material: '',
    Gender: '',
    IsFeatured: false,
    IsTrending: false,
    IsNewArrival: false,
    IsActive: true,
  });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState(null);

  const redirectToList = (message) => {
    navigate('/admin/bundleproduct', { state: { toast: message } });
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Read location state (coming from ProductCreate redirect)
  useEffect(() => {
    if (location.state?.toast) {
      showToast(location.state.toast, 'success');
      if (location.state?.tab) setActiveTab(location.state.tab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiFetch('/BundleProduct/GetCategoryDropdown')
      .then(({ data }) => setCategories(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setCategories([]));
    apiFetch('/BundleProduct/GetBrandDropdown')
      .then(({ data }) => setBrands(Array.isArray(data?.data) ? data.data : []))
      .catch(() => setBrands([]));
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const { data } = await apiFetch(`/BundleProduct/GetById/${id}`);
        const p = data?.data ?? data;
        setForm({
          CategoryId: p.CategoryId || '',
          BrandId: p.BrandId || '',
          ProductName: p.ProductName || '',
          SKU: p.SKU || '',
          Price: p.Price != null ? Number(p.Price).toFixed(2) : '0.00',
          ShortDescription: p.ShortDescription || '',
          FullDescription: p.FullDescription || '',
          Material: p.Material || '',
          Gender: p.Gender || '',
          IsFeatured: p.IsFeatured || false,
          IsTrending: p.IsTrending || false,
          IsNewArrival: p.IsNewArrival || false,
          IsActive: p.IsActive !== undefined ? p.IsActive : true,
        });
      } catch (e) {
        setLoadError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchProduct();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    if (!form.ProductName.trim()) { setFormError('Product Name is required.'); setActiveTab('details'); return; }
    if (!form.CategoryId) { setFormError('Please select a Category.'); setActiveTab('details'); return; }
    if (!form.SKU.trim()) { setFormError('SKU is required.'); setActiveTab('details'); return; }

    setSaving(true);
    try {
      await apiFetch('/BundleProduct/Update', {
        method: 'PUT',
        data: {
          BundleProductId: Number(id),
          CategoryId: Number(form.CategoryId),
          BrandId: form.BrandId ? Number(form.BrandId) : 0,
          ProductName: form.ProductName,
          SKU: form.SKU,
          Price: parseFloat(form.Price) || 0,
          ShortDescription: form.ShortDescription,
          FullDescription: form.FullDescription,
          Material: form.Material,
          Gender: form.Gender,
          IsFeatured: form.IsFeatured,
          IsTrending: form.IsTrending,
          IsNewArrival: form.IsNewArrival,
          IsActive: form.IsActive,
          UpdatedBy: 0,
        },
      });
      redirectToList('Bundle product updated successfully.');
    } catch (e) {
      setFormError(e.message);
      setActiveTab('details');
    } finally {
      setSaving(false);
    }
  };

  const handleTabSave = async () => {
    if (activeTab === 'details') {
      await handleSubmit();
      return;
    }

    const tabMessages = {
      images: 'Bundle images saved successfully.',
      attributes: 'Bundle attribute values saved successfully.',
      relations: 'Products in bundle saved successfully.',
    };
    redirectToList(tabMessages[activeTab] || 'Bundle product saved successfully.');
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <AdminLink to="/admin/bundleproduct">Bundle Products</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Edit Bundle Product</span>
      </p>

      <div className="row">
        <div className="col-lg-12 col-md-10 col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title mb-1">Edit Bundle Product</h4>
              <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>Update bundle product details</p>

              {/* Toast */}
              {toast && (
                <div
                  className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`}
                  role="alert"
                  style={{ fontSize: '0.875rem' }}
                >
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} aria-label="Close" />
                </div>
              )}

              {/* Tabs */}
              <div className="d-sm-flex justify-content-between align-items-center transaparent-tab-border mb-4">
                <ul className="nav nav-tabs tab-transparent" role="tablist">
                  <li className="nav-item">
                    <button
                      className={`nav-link${activeTab === 'details' ? ' active' : ''}`}
                      role="tab"
                      onClick={() => setActiveTab('details')}
                    >
                      <i className="mdi mdi-clipboard-text me-1"></i>Bundle Details
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link${activeTab === 'images' ? ' active' : ''}`}
                      role="tab"
                      onClick={() => setActiveTab('images')}
                    >
                      <i className="mdi mdi-image-multiple me-1"></i>Bundle Images
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link${activeTab === 'attributes' ? ' active' : ''}`}
                      role="tab"
                      onClick={() => setActiveTab('attributes')}
                    >
                      <i className="mdi mdi-tag-multiple me-1"></i>Attribute Values
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link${activeTab === 'relations' ? ' active' : ''}`}
                      role="tab"
                      onClick={() => setActiveTab('relations')}
                    >
                      <i className="mdi mdi-link-variant me-1"></i>Products in Bundle
                    </button>
                  </li>
                </ul>
              </div>

              {activeTab === 'images' ? (
                loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <BundleProductImageManage bundleProductId={id} />
                )
              ) : activeTab === 'relations' ? (
                <BundleProductRelationManage bundleProductId={id} />
              ) : activeTab === 'attributes' ? (
                <BundleProductAttributeValuePage embedded={true} bundleProductId={id} />
              ) : loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : loadError ? (
                <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
                  <i className="mdi mdi-alert-circle me-1"></i>{loadError}
                </div>
              ) : (
                <form id="product-details-form" onSubmit={handleSubmit}>
                  {formError && (
                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                      <i className="mdi mdi-alert-circle me-1"></i>{formError}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Product Name <span className="text-danger">*</span></label>
                        <input className="form-control" name="ProductName" value={form.ProductName} onChange={handleChange} placeholder="Product Name" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">SKU <span className="text-danger">*</span></label>
                        <input className="form-control" name="SKU" value={form.SKU} onChange={handleChange} placeholder="SKU" />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Price</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', pointerEvents: 'none', fontSize: '0.9rem', zIndex: 1 }}>{currencySymbol}</span>
                          <input
                            className="form-control"
                            style={{ paddingLeft: 24 }}
                            type="text"
                            inputMode="decimal"
                            name="Price"
                            value={form.Price}
                            onChange={handleChange}
                            onFocus={e => e.target.select()}
                            onBlur={e => {
                              const val = parseFloat(e.target.value);
                              setForm(prev => ({ ...prev, Price: isNaN(val) ? '0.00' : val.toFixed(2) }));
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Category <span className="text-danger">*</span></label>
                        <select className="form-control" name="CategoryId" value={form.CategoryId} onChange={handleChange}>
                          <option value="">— Select Category —</option>
                          {categories.map(c => (
                            <option key={c.CategoryId} value={c.CategoryId}>{c.CategoryName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Brand</label>
                        <select className="form-control" name="BrandId" value={form.BrandId} onChange={handleChange}>
                          <option value="">— Select Brand —</option>
                          {brands.map(b => (
                            <option key={b.BrandId} value={b.BrandId}>{b.BrandName}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Short Description</label>
                        <input className="form-control" name="ShortDescription" value={form.ShortDescription} onChange={handleChange} placeholder="Short Description" />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Material</label>
                        <input className="form-control" name="Material" value={form.Material} onChange={handleChange} placeholder="Material" />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label className="form-label">Gender</label>
                        <input className="form-control" name="Gender" value={form.Gender} onChange={handleChange} placeholder="Gender" />
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">Full Description</label>
                    <textarea className="form-control" name="FullDescription" value={form.FullDescription} onChange={handleChange} placeholder="Full Description" rows={4} />
                  </div>

                  <div className="d-flex flex-wrap gap-3 mb-3">
                    {[
                      { name: 'IsActive', label: 'Active' },
                    ].map(({ name, label }) => (
                      <div className="form-check" key={name}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name={name}
                          id={`edit_${name}`}
                          checked={form[name]}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor={`edit_${name}`}>{label}</label>
                      </div>
                    ))}
                  </div>
                </form>
              )}

              {/* Submit / Cancel — all tabs */}
              {!loading && !loadError && (
                <div className="d-flex gap-2 mt-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={handleTabSave}
                  >
                    {saving && activeTab === 'details' ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin/bundleproduct')}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BundleProductEdit;
